import { createSlice } from '@reduxjs/toolkit';
import { BASE_END_POINT_PLAN, BASE_END_POINT_USER, PLAN_END_POINT } from '../../../config';
import { getLocalStorageAuthentication } from '../../../lib/auth';

const draftInitial = {};

const initialState = {
  current: null,
  draft: draftInitial,
  userPlans: [],
  isLoading: false,
  error: null,
  hasFetched: false,
  planStatus: null, // User's current plan status with expiry info
  planStatusLoading: false,
  planStatusError: null,
  planStatusFetched: false
};

const plansSlice = createSlice({
  name: 'plans',
  initialState,
  reducers: {
    updateDraft(state, action) {
      state.draft = action.payload.plan;
    },
    // Thunk reducers
    fetchPlansRequest(state) {
      state.isLoading = true;
      state.error = null;
    },
    fetchPlansSuccess(state, action) {
      state.isLoading = false;
      state.userPlans = action.payload.plans;
      state.error = null;
      state.hasFetched = true;
    },
    fetchPlansFailure(state, action) {
      state.isLoading = false;
      state.error = action.payload.error;
      state.hasFetched = true;
    },
    fetchPlanStatusRequest(state) {
      state.planStatusLoading = true;
      state.planStatusError = null;
    },
    fetchPlanStatusSuccess(state, action) {
      state.planStatusLoading = false;
      state.planStatus = action.payload.planStatus;
      state.planStatusError = null;
      state.planStatusFetched = true;
    },
    fetchPlanStatusFailure(state, action) {
      state.planStatusLoading = false;
      state.planStatusError = action.payload.error;
      state.planStatusFetched = true;
    },
    resetPlanStatusFetched(state) {
      state.planStatusFetched = false;
      console.log('🔄 [Plans Slice] Reset planStatusFetched flag - can fetch again');
    }
  }
});

export const draftPlanSelector = (state) => state.plans.draft;
export const userPlansSelector = (state) => state.plans.userPlans;
export const plansLoadingSelector = (state) => state.plans.isLoading;
export const plansErrorSelector = (state) => state.plans.error;
export const planStatusSelector = (state) => state.plans.planStatus;
export const planStatusLoadingSelector = (state) => state.plans.planStatusLoading;
export const planStatusErrorSelector = (state) => state.plans.planStatusError;
export const planStatusFetchedSelector = (state) => state.plans.planStatusFetched;

export const {
  updateDraft,
  fetchPlansRequest,
  fetchPlansSuccess,
  fetchPlansFailure,
  fetchPlanStatusRequest,
  fetchPlanStatusSuccess,
  fetchPlanStatusFailure,
  resetPlanStatusFetched
} = plansSlice.actions;

// Thunk to fetch plans by user ID
export const fetchPlansByUserId =
  ({ userId }) =>
  async (dispatch, getState) => {
    const state = getState();
    const isLoading = state.plans.isLoading;
    const hasFetched = state.plans.hasFetched;

    // Skip if currently loading
    if (isLoading) {
      console.log('🔄 [Plans Thunk] Already loading - skipping');
      return;
    }

    // Skip if already fetched (regardless of whether we got data or not)
    if (hasFetched) {
      console.log('🔄 [Plans Thunk] Already fetched plans - skipping');
      return;
    }

    console.log('🔍 [Plans Thunk] Fetching plans for userId:', userId);

    dispatch(fetchPlansRequest());

    try {
      const localAuth = getLocalStorageAuthentication();
      const userIdEncoded = encodeURIComponent(userId);
      const url = `${BASE_END_POINT_PLAN}${PLAN_END_POINT}/user/${userIdEncoded}`;

      console.log('🌐 [Plans Thunk] Fetching from:', url);
      console.log('🔑 [Plans Thunk] Using JWT token:', localAuth.token ? 'Present' : 'Missing');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        }
      });

      if (!response.ok) {
        // Handle 404 or empty state gracefully
        if (response.status === 404) {
          console.log('📭 [Plans Thunk] No plans found for user (404)');
          dispatch(fetchPlansSuccess({ plans: [] }));
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const plans = data?.data || [];

      console.log('✅ [Plans Thunk] Received', plans.length, 'plans');

      dispatch(fetchPlansSuccess({ plans }));
      return plans;
    } catch (error) {
      console.error('❌ [Plans Thunk] Failed to fetch plans:', error);
      dispatch(fetchPlansFailure({ error: error.message }));
      // Don't throw - just log and set error state to prevent infinite loops
      return [];
    }
  };

// Thunk to fetch user's plan status
export const fetchPlanStatus = () => async (dispatch, getState) => {
  const state = getState();
  const isLoading = state.plans.planStatusLoading;
  const hasFetched = state.plans.planStatusFetched;

  // Skip if currently loading
  if (isLoading) {
    console.log('🔄 [Plan Status Thunk] Already loading - skipping');
    return;
  }

  // Skip if already fetched (optional - remove if you want to allow refresh)
  if (hasFetched) {
    console.log('🔄 [Plan Status Thunk] Already fetched - skipping');
    return;
  }

  console.log('🔍 [Plan Status Thunk] Fetching plan status...');

  dispatch(fetchPlanStatusRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      console.warn('⚠️ [Plan Status Thunk] No authentication token found');
      // Set default "no plan" state
      const defaultPlanStatus = {
        currentPlanId: 0,
        planName: 'No Plan',
        hasActivePlan: false,
        availableUpgrades: [9, 10, 11]
      };
      dispatch(fetchPlanStatusSuccess({ planStatus: defaultPlanStatus }));
      return defaultPlanStatus;
    }

    const url = `${BASE_END_POINT_USER}/api/v1/user/plan-status`;

    console.log('🌐 [Plan Status Thunk] Fetching from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      console.warn(`⚠️ [Plan Status Thunk] HTTP error! status: ${response.status}`);
      // Set default "no plan" state instead of throwing error
      const defaultPlanStatus = {
        currentPlanId: 0,
        planName: 'No Plan',
        hasActivePlan: false,
        availableUpgrades: [9, 10, 11]
      };
      dispatch(fetchPlanStatusSuccess({ planStatus: defaultPlanStatus }));
      return defaultPlanStatus;
    }

    const result = await response.json();

    console.log('✅ [Plan Status Thunk] Plan status fetched:', result);

    if (result.message === 'Success' && result.data) {
      console.log('✅ [Plan Status Thunk] Setting plan status:', result.data);
      dispatch(fetchPlanStatusSuccess({ planStatus: result.data }));
      return result.data;
    } else {
      // Set default "no plan" state
      console.warn('⚠️ [Plan Status Thunk] Unexpected response format, using default');
      const defaultPlanStatus = {
        currentPlanId: 0,
        planName: 'No Plan',
        hasActivePlan: false,
        availableUpgrades: [9, 10, 11]
      };
      dispatch(fetchPlanStatusSuccess({ planStatus: defaultPlanStatus }));
      return defaultPlanStatus;
    }
  } catch (error) {
    console.error('❌ [Plan Status Thunk] Error fetching plan status:', error);
    // Set default "no plan" state
    const defaultPlanStatus = {
      currentPlanId: 0,
      planName: 'No Plan',
      hasActivePlan: false,
      availableUpgrades: [9, 10, 11]
    };
    dispatch(fetchPlanStatusSuccess({ planStatus: defaultPlanStatus }));
    dispatch(fetchPlanStatusFailure({ error: error.message }));
    return defaultPlanStatus;
  }
};

// Force refresh plan status - bypasses hasFetched guard by resetting it first
// Use this after payment completes to immediately update UI
export const forceRefreshPlanStatus = () => async (dispatch) => {
  console.log('🔄 [Plan Status Thunk] Force refresh - resetting planStatusFetched flag');
  dispatch(resetPlanStatusFetched());
  return dispatch(fetchPlanStatus());
};

export default plansSlice.reducer;
